import { toggleGeneralAIOnboarding } from '@affine/core/components/affine/ai-onboarding/apis';
import type { AIModelService } from '@affine/core/modules/ai-button/services/models';
import {
  type AuthAccountInfo,
  type AuthService,
  type AvatarAssetCacheInput,
  type AvatarAssetCacheService,
  type ServerService,
  toAbsoluteAvatarAssetUrl,
} from '@affine/core/modules/cloud';
import type { GlobalDialogService } from '@affine/core/modules/dialogs';
import {
  type AddContextFileInput,
  ContextCategories,
  type ContextWorkspaceEmbeddingStatus,
  type getCopilotHistoriesQuery,
  type QueryChatSessionsInput,
  type RequestOptions,
  type UpdateChatSessionInput,
} from '@affine/graphql';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { z } from 'zod';

import { transformHistoryAttachments } from '../utils/blob-url-resolver';
import { getAIChatOnlineStatus } from '../utils/chat-connectivity';
import { ALL_TEXT_ACTIONS, CHAT_ACTION } from './ai-actions';
import { AIProvider } from './ai-provider';
import { type CopilotClient, Endpoint } from './copilot-client';
import { GeneralNetworkError } from './error';
import type { PromptKey } from './prompt';
import { textToText, toImage } from './request';
import { setupTracker } from './tracker';

function toAIUserInfo(account: AuthAccountInfo | null) {
  if (!account) return null;
  return {
    avatarUrl: account.avatar ?? '',
    email: account.email ?? '',
    id: account.id,
    name: account.label,
  };
}

async function toCachedAIUserInfo(
  account: AuthAccountInfo | null,
  avatarCache?: AvatarAssetCacheService,
  serverService?: ServerService
) {
  const userInfo = toAIUserInfo(account);
  if (!account || !userInfo) return null;
  let avatarUrl = account.avatar ?? '';
  const sourceUrl =
    account.avatar && serverService
      ? toAbsoluteAvatarAssetUrl(account.avatar, serverService.server.baseUrl)
      : null;

  if (avatarCache && serverService && sourceUrl) {
    const input: AvatarAssetCacheInput = {
      kind: 'user-avatar',
      scope: {
        accountId: account.id,
        serverId: serverService.server.id,
      },
      entityId: account.id,
      source: sourceUrl,
    };
    avatarUrl =
      (await avatarCache
        .objectUrl(input, serverService.server.baseUrl)
        .catch(() => null)) ??
      account.avatar ??
      '';
  }

  return {
    ...userInfo,
    avatarUrl,
  };
}

const filterStyleToPromptName = new Map<string, PromptKey>(
  Object.entries({
    'Clay style': 'Convert to Clay style',
    'Pixel style': 'Convert to Pixel style',
    'Sketch style': 'Convert to Sketch style',
    'Anime style': 'Convert to Anime style',
  })
);

const processTypeToPromptName = new Map<string, PromptKey>(
  Object.entries({
    Clearer: 'Upscale image',
    'Remove background': 'Remove background',
    'Convert to sticker': 'Convert to sticker',
  })
);

export function setupAIProvider(
  client: CopilotClient,
  globalDialogService: GlobalDialogService,
  authService: AuthService,
  workspaceEngine: {
    embeddingWorkspaceProgress$:
      | import('rxjs').Observable<{
          docsIndexed: number;
          docsTotal: number;
        }>
      | null;
    embeddingDocStatus$:
      | import('rxjs').Observable<{
          docId: string;
          status: string;
        }>
      | null;
  },
  aiModelService?: AIModelService,
  avatarCache?: AvatarAssetCacheService,
  serverService?: ServerService
) {
  const assertAIChatOnline = () => {
    if (!getAIChatOnlineStatus()) {
      throw new GeneralNetworkError(
        `NETWORK_ERROR: ${BRAND_DISPLAY_NAME} AI is not available without a server connection.`
      );
    }
  };

  const resolveModelId = async (options: {
    modelId?: string;
    workspaceId?: string;
  }) => {
    if (options.modelId) return options.modelId;
    if (!aiModelService || !options.workspaceId) return undefined;

    const modelId = await aiModelService.resolveChatModelIdForWorkspace(
      options.workspaceId
    );
    if (!modelId) {
      throw new GeneralNetworkError(
        `AI_MODEL_UNAVAILABLE: ${BRAND_DISPLAY_NAME} AI is not available in this workspace because no chat model is configured.`
      );
    }
    return modelId;
  };

  async function createSession({
    promptName,
    workspaceId,
    docId,
    sessionId,
    retry,
    pinned,
    reuseLatestChat,
  }: BlockSuitePresets.AICreateSessionOptions) {
    if (sessionId) return sessionId;
    if (retry) return AIProvider.LAST_ACTION_SESSIONID;

    return client.createSession({
      workspaceId,
      docId,
      promptName,
      pinned,
      reuseLatestChat,
    });
  }

  AIProvider.provide('userInfo', () => {
    return toAIUserInfo(authService.session.account$.value);
  });

  let accountGeneration = 0;
  const accountSubscription = authService.session.account$.subscribe(
    account => {
      const generation = ++accountGeneration;
      void toCachedAIUserInfo(account, avatarCache, serverService).then(
        userInfo => {
          if (generation === accountGeneration) {
            AIProvider.slots.userInfo.next(userInfo);
          }
        }
      );
    }
  );

  //#region actions

  // Chat action - special handling for webSearch
  AIProvider.provide('chat', async options => {
    const { input } = options;
    assertAIChatOnline();
    const modelId = await resolveModelId(options);

    const sessionId = await createSession({
      promptName: CHAT_ACTION.prompt,
      ...options,
    });
    return textToText({
      ...options,
      modelId,
      client,
      sessionId,
      threadId: sessionId, // threadId = sessionId, always!
      content: input,
      timeout: CHAT_ACTION.timeout,
      endpoint: CHAT_ACTION.endpoint,
      // sl-ddz8.5.3: Pass currently selected bot to backend
      botId: AIProvider.currentBotId ?? undefined,
    });
  });

  // Data-driven text actions - one loop instead of 20+ blocks
  for (const [actionName, config] of Object.entries(ALL_TEXT_ACTIONS)) {
    // Type assertion needed because AIProvider.provide expects specific action keys
    (AIProvider.provide as any)(actionName, async (options: any) => {
      assertAIChatOnline();
      // expandMindmap requires input validation
      if (actionName === 'expandMindmap' && !options.input) {
        throw new Error('expandMindmap action requires input');
      }

      const sessionId = await createSession({
        promptName: config.prompt,
        ...options,
      });
      const modelId = await resolveModelId(options);

      return textToText({
        ...options,
        client,
        sessionId,
        threadId: sessionId, // threadId = sessionId, always!
        content: options.input,
        ...(config.timeout ? { timeout: config.timeout } : {}),
        ...(config.endpoint ? { endpoint: config.endpoint } : {}),
        ...(config.mapContext ? config.mapContext(options) : {}),
        modelId,
      });
    });
  }

  AIProvider.provide('makeItReal', async options => {
    assertAIChatOnline();
    let promptName: PromptKey = 'Make it real';
    let content = options.input || '';

    // wireframes
    if (options.attachments?.length) {
      content = `Here are the latest wireframes. Could you make a new website based on these wireframes and notes and send back just the html file?
Here are our design notes:\n ${content}.`;
    } else {
      // notes
      promptName = 'Make it real with text';
      content = `Here are the latest notes: \n ${content}.
Could you make a new website based on these notes and send back just the html file?`;
    }

    const sessionId = await createSession({
      promptName,
      ...options,
    });
    const modelId = await resolveModelId(options);

    return textToText({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content,
      modelId,
    });
  });

  AIProvider.provide('createSlides', async options => {
    assertAIChatOnline();
    const SlideSchema = z.object({
      page: z.number(),
      type: z.enum(['name', 'title', 'content']),
      content: z.string(),
    });
    type Slide = z.infer<typeof SlideSchema>;
    const parseJson = (json: string) => {
      try {
        return SlideSchema.parse(JSON.parse(json));
      } catch {
        return null;
      }
    };
    // TODO(@darkskygit): move this to backend's workflow after workflow support custom code action
    const postfix = (text: string): string => {
      const slides = text
        .split('\n')
        .map(parseJson)
        .filter((v): v is Slide => !!v);
      return slides
        .map(slide => {
          if (slide.type === 'name') {
            return `- ${slide.content}`;
          } else if (slide.type === 'title') {
            return `  - ${slide.content}`;
          } else if (slide.content.includes('\n')) {
            return slide.content
              .split('\n')
              .map(c => `    - ${c}`)
              .join('\n');
          } else {
            return `    - ${slide.content}`;
          }
        })
        .join('\n');
    };
    const sessionId = await createSession({
      promptName: 'workflow:presentation',
      ...options,
    });
    const modelId = await resolveModelId(options);
    return textToText({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content: options.input,
      timeout: 180000,
      endpoint: Endpoint.Workflow,
      postfix,
      modelId,
    });
  });

  // Image actions - special handling with toImage
  AIProvider.provide('createImage', async options => {
    assertAIChatOnline();
    const sessionId = await createSession({
      promptName: 'Generate image',
      ...options,
    });
    return toImage({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content:
        !options.input && options.attachments
          ? 'Make the image more detailed.'
          : options.input,
      timeout: 300000,
    });
  });

  AIProvider.provide('filterImage', async options => {
    assertAIChatOnline();
    const promptName: PromptKey | undefined = filterStyleToPromptName.get(
      options.style
    );
    if (!promptName) {
      throw new Error('filterImage requires a promptName');
    }
    const sessionId = await createSession({
      promptName,
      ...options,
    });
    const isWorkflow = !!promptName?.startsWith('workflow:');
    return toImage({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content: options.input,
      timeout: 180000,
      endpoint: isWorkflow ? Endpoint.Workflow : Endpoint.Images,
    });
  });

  AIProvider.provide('processImage', async options => {
    assertAIChatOnline();
    const promptName: PromptKey | undefined = processTypeToPromptName.get(
      options.type
    );
    if (!promptName) {
      throw new Error('processImage requires a promptName');
    }
    const sessionId = await createSession({
      promptName,
      ...options,
    });
    return toImage({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content: options.input,
      timeout: 180000,
    });
  });

  // NOTE: generateCaption and continueWriting are handled by ALL_TEXT_ACTIONS loop
  //#endregion

  AIProvider.provide('session', {
    createSession,
    getSession: async (workspaceId: string, sessionId: string) => {
      return client.getSession(workspaceId, sessionId);
    },
    getSessions: async (
      workspaceId: string,
      docId?: string,
      options?: QueryChatSessionsInput
    ) => {
      return client.getSessions(workspaceId, {}, docId, options);
    },
    getRecentSessions: async (
      workspaceId: string,
      limit?: number,
      offset?: number
    ) => {
      return client.getRecentSessions(workspaceId, limit, offset);
    },
    updateSession: async (options: UpdateChatSessionInput) => {
      return client.updateSession(options);
    },
  });

  AIProvider.provide('context', {
    createContext: async (workspaceId: string, sessionId: string) => {
      return client.createContext(workspaceId, sessionId);
    },
    getContextId: async (workspaceId: string, sessionId: string) => {
      return client.getContextId(workspaceId, sessionId);
    },
    addContextDoc: async (options: { contextId: string; docId: string }) => {
      return client.addContextDoc(options);
    },
    removeContextDoc: async (options: { contextId: string; docId: string }) => {
      return client.removeContextDoc(options);
    },
    addContextFile: async (file: File, options: AddContextFileInput) => {
      return client.addContextFile(file, options);
    },
    removeContextFile: async (options: {
      contextId: string;
      fileId: string;
    }) => {
      return client.removeContextFile(options);
    },
    addContextTag: async (options: {
      contextId: string;
      tagId: string;
      docIds: string[];
    }) => {
      return client.addContextCategory({
        contextId: options.contextId,
        type: ContextCategories.Tag,
        categoryId: options.tagId,
        docs: options.docIds,
      });
    },
    removeContextTag: async (options: { contextId: string; tagId: string }) => {
      return client.removeContextCategory({
        contextId: options.contextId,
        type: ContextCategories.Tag,
        categoryId: options.tagId,
      });
    },
    addContextCollection: async (options: {
      contextId: string;
      collectionId: string;
      docIds: string[];
    }) => {
      return client.addContextCategory({
        contextId: options.contextId,
        type: ContextCategories.Collection,
        categoryId: options.collectionId,
        docs: options.docIds,
      });
    },
    removeContextCollection: async (options: {
      contextId: string;
      collectionId: string;
    }) => {
      return client.removeContextCategory({
        contextId: options.contextId,
        type: ContextCategories.Collection,
        categoryId: options.collectionId,
      });
    },
    getContextDocsAndFiles: async (
      workspaceId: string,
      sessionId: string,
      contextId: string
    ) => {
      return client.getContextDocsAndFiles(workspaceId, sessionId, contextId);
    },
    // sl-00ro: Event-driven embedding status via nbstore WebSocket (replaces 10s GraphQL polling)
    pollEmbeddingStatus: async (
      workspaceId: string,
      onPoll: (result: ContextWorkspaceEmbeddingStatus) => void,
      abortSignal: AbortSignal
    ) => {
      // Initial fetch for current state
      const initialResult = await client.getEmbeddingStatus(workspaceId);
      if (abortSignal.aborted) return;
      onPoll(initialResult);

      // Subscribe to nbstore embedding events for live updates
      const subscriptions: Array<{ unsubscribe: () => void }> = [];
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;

      const debouncedRefetch = () => {
        if (abortSignal.aborted) return;
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          if (abortSignal.aborted) return;
          client
            .getEmbeddingStatus(workspaceId)
            .then(result => {
              if (!abortSignal.aborted) onPoll(result);
            })
            .catch(() => {
              // Silently ignore fetch errors — next event will retry
            });
        }, 500);
      };

      const progress$ = workspaceEngine.embeddingWorkspaceProgress$;
      if (progress$) {
        subscriptions.push(progress$.subscribe(debouncedRefetch));
      }

      const docStatus$ = workspaceEngine.embeddingDocStatus$;
      if (docStatus$) {
        subscriptions.push(docStatus$.subscribe(debouncedRefetch));
      }

      // Keep alive until aborted (matches original contract)
      await new Promise<void>(resolve => {
        if (abortSignal.aborted) {
          resolve();
          return;
        }
        abortSignal.addEventListener('abort', () => {
          for (const sub of subscriptions) sub.unsubscribe();
          if (debounceTimer) clearTimeout(debounceTimer);
          resolve();
        });
      });
    },
    matchContext: async (
      content: string,
      contextId?: string,
      workspaceId?: string,
      limit?: number,
      scopedThreshold?: number,
      threshold?: number
    ) => {
      return client.matchContext(
        content,
        contextId,
        workspaceId,
        limit,
        scopedThreshold,
        threshold
      );
    },
    addContextBlob: async (options: { blobId: string; contextId: string }) => {
      return client.addContextBlob({
        contextId: options.contextId,
        blobId: options.blobId,
      });
    },
    removeContextBlob: async (options: {
      blobId: string;
      contextId: string;
    }) => {
      return client.removeContextBlob({
        contextId: options.contextId,
        blobId: options.blobId,
      });
    },
  });

  AIProvider.provide('histories', {
    actions: async (
      workspaceId: string,
      docId: string
    ): Promise<BlockSuitePresets.AIHistory[]> => {
      // @ts-expect-error - 'action' is missing in server impl
      return (
        (await client.getHistories(workspaceId, {}, docId, {
          action: true,
          withPrompt: true,
          withMessages: true,
        })) ?? []
      );
    },
    chats: async (
      workspaceId: string,
      sessionId: string,
      docId?: string
    ): Promise<BlockSuitePresets.AIHistory[]> => {
      const histories =
        (await client.getHistories(workspaceId, {}, docId, {
          sessionId,
          withMessages: true,
        })) ?? [];

      // Transform blob IDs to loadable URLs for image attachments
      // Type assertion needed because transformHistoryAttachments preserves structure
      return histories.map(history =>
        transformHistoryAttachments(history)
      ) as BlockSuitePresets.AIHistory[];
    },
    cleanup: async (
      workspaceId: string,
      docId: string | undefined,
      sessionIds: string[]
    ) => {
      await client.cleanupSessions({ workspaceId, docId, sessionIds });
    },
    ids: async (
      workspaceId: string,
      docId?: string,
      options?: RequestOptions<
        typeof getCopilotHistoriesQuery
      >['variables']['options']
    ): Promise<BlockSuitePresets.AIHistoryIds[]> => {
      // @ts-expect-error - 'action' is missing in server impl
      return await client.getHistoryIds(workspaceId, {}, docId, options);
    },
  });

  AIProvider.provide('photoEngine', {
    async searchImages(options): Promise<string[]> {
      let url = '/api/copilot/unsplash/photos';
      if (options.query) {
        url += `?query=${encodeURIComponent(options.query)}`;
      }
      const result: {
        results?: {
          urls: {
            regular: string;
          };
        }[];
      } = await client.fetcher(url.toString()).then(res => res.json());
      if (!result.results) return [];
      return result.results.map(r => {
        const url = new URL(r.urls.regular);
        url.searchParams.set('fit', 'crop');
        url.searchParams.set('crop', 'edges');
        url.searchParams.set('dpr', (window.devicePixelRatio ?? 2).toString());
        url.searchParams.set('w', `${options.width}`);
        url.searchParams.set('h', `${options.height}`);
        return url.toString();
      });
    },
  });

  AIProvider.provide('onboarding', toggleGeneralAIOnboarding);

  AIProvider.provide('forkChat', options => {
    return client.forkSession(options);
  });

  const disposeRequestLoginHandler = AIProvider.slots.requestLogin.subscribe(
    () => {
      globalDialogService.open('sign-in', {});
    }
  );

  setupTracker();

  return () => {
    disposeRequestLoginHandler.unsubscribe();
    accountSubscription.unsubscribe();
  };
}
